import axios, { AxiosError } from 'axios';

import CustomerAccountService from 'root/apps/QuoteMachineIntegration/services/CustomerAccountService';
import { datadogRum } from 'root/datadog/datadogRum';

import CreditAccountService, { CustomerData } from './CreditAccountService';

/**
 * Gets or creates a credit account ID for a customer.
 * If the customer doesn't have a credit account, creates one with $0 limit on R-Series.
 *
 * @param creditAccountId The existing credit account ID (can be empty/0)
 * @param customerId The customer ID (required if creditAccountId is empty/0)
 * @returns The credit account ID as a string
 */
export async function getOrCreateCreditAccountId(
  creditAccountId: string | number,
  customerId: string
): Promise<string> {
  if (creditAccountId && creditAccountId !== 0 && creditAccountId !== '0') {
    return `${creditAccountId}`;
  }

  if (!customerId) {
    throw new Error('customerId is required when creditAccountId is not provided');
  }

  const account = await CustomerAccountService.ensureCreditAccountExists(customerId);
  return account.CreditAccount.creditAccountRef;
}

/**
 * Checks if an Axios error is a "credit account not found" 404 error.
 */
export function isCreditAccountNotFoundError(error: AxiosError): boolean {
  const errorMessage =
    (error.response?.data as { error?: { message?: string }; message?: string })?.error?.message ||
    (error.response?.data as { message?: string })?.message ||
    '';
  return error.response?.status === 404 && errorMessage.toLowerCase().includes('credit account');
}

export interface PutWithCreditAccountRetryParams<TPayload> {
  url: string;
  payload: TPayload;
  creditAccountIdStr: string;
  creditAccountRef: string;
  transactionRef: string;
  currency: string;
  customer?: CustomerData;
  /** Used in datadog log messages, e.g. "deposit instruction", "deposit usage intent" */
  operationName: string;
  /** Extra context fields for datadog error logging (e.g. { amount } or { totalAmount }) */
  datadogContext?: Record<string, unknown>;
}

/**
 * Executes a PUT request with automatic retry on credit-account-not-found 404 errors.
 *
 * If the PUT returns a 404 whose message mentions "credit account" and customer data is provided,
 * this will create the credit account on Invoicing and retry the PUT once.
 *
 * If the 404 is a credit-account-not-found but no customer data is available, throws an explicit
 * error since the account cannot be created without customer data.
 *
 * All other errors are logged to DatadogRUM with the provided context and rethrown.
 *
 * @returns The response data merged with { creditAccountId }
 */
export async function putWithCreditAccountRetry<TPayload, TResponse>(
  params: PutWithCreditAccountRetryParams<TPayload>
): Promise<TResponse & { creditAccountId: string }> {
  const {
    url,
    payload,
    creditAccountIdStr,
    creditAccountRef,
    transactionRef,
    currency,
    customer,
    operationName,
    datadogContext = {},
  } = params;

  try {
    const { data } = await axios.put<TResponse>(url, payload);
    return { ...data, creditAccountId: creditAccountIdStr };
  } catch (error) {
    const isNotFound = isCreditAccountNotFoundError(error as AxiosError);

    if (isNotFound) {
      if (!customer) {
        throw new Error(
          'Customer data is required to create credit account on Invoicing when it does not exist'
        );
      }

      try {
        await CreditAccountService.createCreditAccount(creditAccountIdStr, 0, customer, currency);
        const { data } = await axios.put<TResponse>(url, payload);
        return { ...data, creditAccountId: creditAccountIdStr };
      } catch (retryError) {
        datadogRum.addError(retryError, {
          creditAccountRef,
          transactionRef,
          currency,
          ...datadogContext,
          message: `Failed to create ${operationName} after creating credit account for transactionRef: ${transactionRef}`,
          timestamp: new Date().toISOString(),
        });
        throw retryError;
      }
    }

    datadogRum.addError(error, {
      creditAccountRef,
      transactionRef,
      currency,
      ...datadogContext,
      message: `Failed to create or update ${operationName} for transactionRef: ${transactionRef}`,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
}
